import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, filter, forkJoin, interval, map, Observable, of, shareReplay, Subscription, take } from 'rxjs'
import { switchMap, tap } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { Sport } from '../../pages/sports/types/sports.type'
import { HIGHLIGHT_SPORT_IDS, HIGHLIGHTS_REFRESH_SECONDS } from '../constants/common.constant'
import { BookmakerMarket, BookmakerRunner } from '../types/bookmaker.type'
import { FancyMarket, FancyRunner } from '../types/fancy.type'
import { Market, MarketEvent, MarketEventType, MarketProvider, Runner } from '../types/market.type'
import { MarketStatusWithSnapshot, SpecificCurrencyRateResponse } from '../types/market-service.type'
import { PromotedData } from '../types/promoted.type'
import { ApiResponse, MarketResponse } from '../types/response.type'
import { MarketUpdate } from '../types/websocket-response.type'
import { AuthService } from './auth.service'
import { BetService } from './bet.service'
import { HelperService } from './helper.service'
import { LiveUpdateService } from './live-update.service'
import { NavigationService } from './navigation.service'
import { WebsocketService } from './websocket.service'

const MARKET_URL = environment.marketUrl
const API_URL = environment.apiUrl
const COMPETITION_MARKETS_TOPIC_OWNER = 'competitionMarkets'

@Injectable({
	providedIn: 'root',
})
export class MarketService {
	rawHighlightData$ = new BehaviorSubject<Market[]>([]) // Storage variable for greyhound data

	bfMarketUpdates$ = new BehaviorSubject<Record<string, MarketUpdate>>({}) // Market websocket updates in the format [update-ID]: update

	bookmakerUpdates$ = new BehaviorSubject<Record<string, BookmakerMarket>>({}) // Holds updates for fancy markets. Key is 'topic'

	fancyUpdates$ = new BehaviorSubject<Record<string, FancyMarket>>({}) // Holds updates for fancy markets. Key is 'topic'

	favorites$ = new BehaviorSubject<string[]>([]) // Holds markets that have been marked as favorite

	whiteLabelSports$ = new BehaviorSubject<Sport[] | null>(null) // Holds sports that are subscribed by the user's White Label

	promoted$ = new BehaviorSubject<PromotedData | null>(null) // Holds promoted leagues and events data

	private cachedPromotedData$: Observable<PromotedData> | null = null

	private marketUpdatesSubscription: Subscription | null = null

	constructor(
		private readonly websocketService: WebsocketService,
		private readonly liveUpdateService: LiveUpdateService,
		private readonly httpClient: HttpClient,
		private readonly router: Router,
		private readonly authService: AuthService,
		private readonly translateService: TranslateService,
		private readonly helperService: HelperService,
		private readonly betService: BetService,
		private readonly navigationService: NavigationService,
	) {
		this.reflectMarketUpdates()
		this.updateFavoritesFromLocalStorage()
		this.collectBetMarkets()
	}

	/**
	 * To initialize the necessary steps for market service
	 * - Get highlight data
	 * - Subscribe to market updates
	 * - Create an interval that will refresh the highlights data
	 */
	init() {
		this.betService.fetchCurrentUserOrders().subscribe()
		if (this.rawHighlightData$.getValue().length === 0) {
			if (typeof Worker !== 'undefined') {
				this.prepareHighlightsDataWithWebWorker()
			} else {
				this.prepareHighlightsData()
			}
			this.reflectMarketUpdates()
			interval(HIGHLIGHTS_REFRESH_SECONDS * 1000).subscribe({
				next: () => {
					if (['/home', '/in-play'].includes(this.router.url)) {
						if (typeof Worker !== 'undefined') {
							this.prepareHighlightsDataWithWebWorker()
						} else {
							this.prepareHighlightsData()
						}
					}
				},
			})
		}
	}

	/**
	 * Get sports subscribed by the user's white label
	 */
	getWhiteLabelSports() {
		return this.httpClient.get<MarketResponse<Sport[]>>(`${MARKET_URL}/navigation/sports`).pipe(map((response) => response.data))
	}

	/**
	 * Fetch the highlight markets from an API endpoint
	 */
	prepareHighlightsData() {
		this.getWhiteLabelSports()
			.pipe(
				map((sports) => sports ?? []),
				switchMap((sports) => this.navigationService.getSports(sports)),
				map((sports) => {
					return sports.filter((sport) => HIGHLIGHT_SPORT_IDS.includes(Number(sport.id)))
				}),
				tap({
					next: (sports) => {
						const orderedSports: Sport[] = []
						HIGHLIGHT_SPORT_IDS.forEach((sportsId) => {
							const currentSport = sports.find((sport) => sport.id === sportsId.toString())
							if (currentSport) {
								orderedSports.push(currentSport)
							}
						})
						this.whiteLabelSports$.next(orderedSports.map((sport) => ({ ...sport, name: sport.name.replace(/racing/i, '') })))
					},
				}),
				switchMap((sports) => this.getHighlightsData(sports.map((sport) => sport.id).join(','))),
				map((highlight) => highlight.data?.result ?? []),
				switchMap((result) => this.navigationService.getMarkets(result)),
			)
			.subscribe({
				next: (markets) => {
					this.rawHighlightData$.next(this.unionMarkets(this.rawHighlightData$.getValue(), markets))
				},
			})
	}

	/**
	 * Fetch the highlight markets from an API endpoint
	 */
	prepareHighlightsDataWithWebWorker() {
		// Create a new web-worker
		const highlightsMarketWorker = new Worker(new URL('../web-workers/highlights-market.worker', import.meta.url))

		// Listen on messages sent from the worker
		highlightsMarketWorker.onmessage = (event: MessageEvent<Market[]>) => {
			this.navigationService.getMarkets(event.data).subscribe({
				next: (cleanedMarkets) => {
					this.rawHighlightData$.next(this.unionMarkets(this.rawHighlightData$.getValue(), cleanedMarkets))
					// Terminate worker after processing to prevent memory leak
					highlightsMarketWorker.terminate()
				},
			})
		}

		// Handle worker errors and terminate
		highlightsMarketWorker.onerror = (error) => {
			console.error('Worker error:', error)
			highlightsMarketWorker.terminate()
		}

		// Get the white label sports
		this.getWhiteLabelSports()
			.pipe(
				map((sports) => sports ?? []),
				switchMap((sports) => this.navigationService.getSports(sports)),
				map((sports) => sports.filter((sport) => HIGHLIGHT_SPORT_IDS.includes(Number(sport.id)))),
				map((sports) => {
					// Sports ordered in the correct order
					const orderedSports: Sport[] = []
					HIGHLIGHT_SPORT_IDS.forEach((sportsId) => {
						const currentSport = sports.find((sport) => sport.id === sportsId.toString())
						if (currentSport) {
							orderedSports.push(currentSport)
						}
					})
					this.whiteLabelSports$.next(orderedSports.map((sport) => ({ ...sport, name: sport.name.replace(/racing/i, '') })))

					return orderedSports
				}),
				tap((orderedSports) => {
					// Send the message to the worker
					highlightsMarketWorker.postMessage({
						accessToken: this.authService.getAccessToken(),
						sports: orderedSports,
						locale: this.helperService.locale,
						marketUrl: MARKET_URL,
						maxResults: environment.maxResults,
					})
				}),
			)
			.subscribe()
	}

	/**
	 * Merge market arrays without duplicate
	 * @param initialMarkets
	 * @param newMarkets
	 */
	unionMarkets(initialMarkets: Market[], newMarkets: Market[]): Market[] {
		const marketMap = new Map<string, Market>()

		initialMarkets.forEach((market) => marketMap.set(market.marketId, market))
		newMarkets.forEach((market) => marketMap.set(market.marketId, market))

		return Array.from(marketMap.values())
	}

	mergeBookmakerMarketUpdate(currentMarket: BookmakerMarket, update: BookmakerMarket): BookmakerMarket {
		const incomingRunners = update.runners ?? []
		const updatedRunners = new Map(incomingRunners.map((runner) => [`${runner.selectionId}:${runner.handicap}`, runner]))
		const currentRunnerKeys = new Set(currentMarket.runners.map((runner) => `${runner.selectionId}:${runner.handicap}`))
		const runners = currentMarket.runners.map((runner) => {
			const updatedRunner = updatedRunners.get(`${runner.selectionId}:${runner.handicap}`)
			return updatedRunner ? { ...runner, ...updatedRunner } : runner
		})

		incomingRunners.forEach((runner) => {
			if (!currentRunnerKeys.has(`${runner.selectionId}:${runner.handicap}`)) runners.push(runner)
		})

		return { ...currentMarket, ...update, runners }
	}

	/**
	 * Get highlights for the subscribed sports of the user's white label
	 * Uses the new event-type-markets API for each sport and date
	 */
	getHighlightsData(sportsId: string) {
		const sportIds = sportsId.split(',').filter(Boolean)
		const dates: ('today' | 'tomorrow' | 'dayAfter')[] = ['today', 'tomorrow', 'dayAfter']
		const requests: Observable<Market[]>[] = []

		// Create requests for each sport and each date
		sportIds.forEach((id) => {
			dates.forEach((date) => {
				requests.push(this.fetchEventTypeMarkets(id.trim(), date))
			})
		})

		// Execute all requests in parallel and merge results
		return forkJoin(requests).pipe(
			map((results) => {
				const allMarkets: Market[] = []
				results.forEach((markets) => allMarkets.push(...markets))

				// Deduplicate by marketId
				const uniqueMarkets = this.unionMarkets([], allMarkets)

				return {
					message: 'Success',
					data: {
						result: uniqueMarkets,
					},
				} as MarketResponse<{ result?: Market[] }>
			}),
		)
	}

	/**
	 * Fetch markets for a specific event type (sport)
	 * @param eventTypeId - The sport ID (e.g., '1' for Soccer, '4' for Cricket)
	 * @param date - Optional date filter: 'today', 'tomorrow', or 'dayAfter'
	 */
	fetchEventTypeMarkets(eventTypeId: string, date?: 'today' | 'tomorrow' | 'dayAfter'): Observable<Market[]> {
		const params: Record<string, string | number> = {
			maxResults: environment.maxResults,
			sortBy: 'date',
			locale: this.helperService.locale,
		}

		if (date) {
			params['date'] = date
		}

		return this.httpClient.get<MarketResponse<Market[] | { result?: Market[] }>>(`${MARKET_URL}/bf/event-type-markets/${eventTypeId}`, { params }).pipe(
			map((response) => {
				// Handle both array and wrapped response formats
				if (Array.isArray(response.data)) {
					return response.data
				}
				return (response.data as { result?: Market[] })?.result || []
			}),
		)
	}

	/**
	 * Get promoted leagues and events data
	 */
	getPromotedData() {
		if (!this.cachedPromotedData$) {
			this.cachedPromotedData$ = this.httpClient
				.get<MarketResponse<PromotedData>>(`${MARKET_URL}/navigation/promoted`, {
					params: {
						locale: this.helperService.locale,
					},
				})
				.pipe(
					map((response) => response.data ?? ({ promotedLeagues: [], promotedEvents: [], dates: [] } as PromotedData)),
					shareReplay(1),
				)
		}
		return this.cachedPromotedData$
	}

	/**
	 * Get a list of market IDs that have open or suspended status
	 * @param marketsRecord
	 * @param returnSnapshot
	 */
	getMarketStatusWithSnapshot(marketsRecord: Record<string, { marketId: string }[]>, returnSnapshot: 'Y' | 'N' = 'Y') {
		const marketIds: string[] = []
		Object.values(marketsRecord).forEach((eventDetail) => {
			eventDetail.forEach((homeTabData) => {
				marketIds.push(`bf_${homeTabData?.marketId}`)
			})
		})

		if (marketIds.length === 0) {
			const result: { snapshot: Record<string, MarketUpdate>; statuses: string[] } = { snapshot: {}, statuses: [] }
			return of(result)
		}

		return this.httpClient
			.post<MarketStatusWithSnapshot>(`${MARKET_URL}/market/status`, {
				market_ids: marketIds.join(','),
				statuses: '2,3',
				return_snapshot: returnSnapshot,
			})
			.pipe(
				map((data) => {
					// Put the valid statuses into a plain array of strings
					const tempValidMarketIds: string[] = []

					data.statuses[2]?.forEach((marketId) => {
						tempValidMarketIds.push(marketId)
					})
					data.statuses[3]?.forEach((marketId) => {
						tempValidMarketIds.push(marketId)
					})

					return {
						snapshot: data.snapshot,
						statuses: tempValidMarketIds,
					}
				}),
			)
	}

	/**
	 * Get the currency conversion rate
	 */
	getCurrencyRate(currencyCode: string) {
		return this.httpClient.get<ApiResponse<SpecificCurrencyRateResponse[]>>(`${API_URL}/api/exchange_rate/get_by_currency/${currencyCode}`)
	}

	/**
	 * Reflect the websocket market update
	 */
	reflectMarketUpdates() {
		if (this.marketUpdatesSubscription) return

		// Subscribe to the response and construct the runner data to be displayed
		this.marketUpdatesSubscription = this.websocketService.connectionStatus$
			.pipe(
				filter(Boolean),
				switchMap(() => this.liveUpdateService.getData<MarketUpdate | BookmakerMarket | FancyMarket>()),
				filter((update) => Boolean(update)),
			)
			.subscribe({
				next: (update) => {
					if (this.helperService.marketUpdateIsBfType(update)) {
						this.bfMarketUpdates$.next({
							...this.bfMarketUpdates$.getValue(),
							[update.id]: update,
						})
					}

					if (this.helperService.marketUpdateIsBookmakerType(update)) {
						this.bookmakerUpdates$.next({
							...this.bookmakerUpdates$.getValue(),
							[update.topic]: update,
						})
					}

					if (this.helperService.marketUpdateIsFancyType(update)) {
						this.fancyUpdates$.next({
							...this.fancyUpdates$.getValue(),
							[update.topic]: update,
						})
					}
				},
			})
	}

	/**
	 * Add the snapshot data sent from the status API call into the market update variable.
	 * This will let the UI load initial even before a websocket data is fetched.
	 * @param snapshot
	 */
	setInitialMarketUpdate(snapshot: Record<string, MarketUpdate>) {
		const updateResult = this.bfMarketUpdates$.getValue()
		Object.entries(snapshot).forEach(([key, value]) => (updateResult[key.replace('bf_', '')] = value))
		this.bfMarketUpdates$.next(updateResult)
	}

	/**
	 * Send a request for websocket updates via WebSocket.
	 */
	requestUpdateForMarkets(selectedTabData: Record<string, Market[]>) {
		const marketIds: string[] = []
		const topicOwner = Object.keys(selectedTabData).join(':')
		// Construct array of market IDs from the selected tab's data
		Object.values(selectedTabData).forEach((eventDetails) => {
			eventDetails.forEach((datum) => {
				marketIds.push(`bf_${datum.marketId}`)
			})
		})

		if (marketIds.length > 0) {
			const topicMessage =
				topicOwner === COMPETITION_MARKETS_TOPIC_OWNER
					? { topics: marketIds, topicMode: 'replace' as const }
					: { topics: marketIds, topicMode: 'replaceOwner' as const, topicOwner }

			// Send immediately if connected, otherwise it will be sent on reconnection
			if (this.websocketService.connectionStatus$.value) {
				this.websocketService.sendMessage(topicMessage)
			} else {
				this.websocketService.connectionStatus$.pipe(filter(Boolean), take(1)).subscribe(() => {
					this.websocketService.sendMessage(topicMessage)
				})
			}
		}
	}

	/**
	 * Add or remove a market into/from the favorite list
	 */
	toggleMarketFavoriteButton(key: string) {
		let resultingFavorites = this.helperService.getFavoriteMarkets()

		const marketExists = resultingFavorites.includes(key)

		if (marketExists) {
			resultingFavorites = resultingFavorites.filter((favoriteKey) => favoriteKey !== key)
		} else {
			resultingFavorites = [...resultingFavorites, key]
		}

		this.favorites$.next(resultingFavorites)
		this.helperService.setFavoriteMarkets(resultingFavorites)
	}

	/**
	 * Get the details of the specified markets
	 * @param marketIds
	 */
	getDetailsForMultipleNonPendingBfMarkets(marketIds: string[]) {
		return this.httpClient
			.get<MarketResponse<Market[] | Market>>(`${MARKET_URL}/bf/market_status`, {
				params: {
					ids: marketIds.join(','),
					statuses: '2,3',
				},
			})
			.pipe(map((response) => response.data))
	}

	/**
	 * Get the details of the specified pending markets
	 * @param marketIds
	 */
	getDetailsForPendingBfMarkets(marketIds: string[]) {
		return this.httpClient.post<MarketResponse<Market[]>>(`${MARKET_URL}/bf/markets`, { marketIds }).pipe(map((response) => response.data))
	}

	/**
	 * Get list of markets under the specified event
	 * @param eventId
	 */
	getEventMarkets(eventId: string) {
		return this.httpClient.get<MarketResponse<{ event?: MarketEvent; eventType?: MarketEventType; markets?: Market[] }>>(
			`${MARKET_URL}/navigation/event-market/${eventId}`,
		)
	}

	/**
	 * Get list of markets under Horse Racing and Grey Hound
	 * @param marketId
	 */
	getEventMarketsForHorseRacingAndGreyHound(marketId: string) {
		return this.httpClient.get<MarketResponse<{ event?: MarketEvent; eventType?: MarketEventType; markets?: Market[] }>>(
			`${MARKET_URL}/navigation/race-market/${marketId}`,
		)
	}

	/**
	 * Get in-play markets from the API endpoint
	 * @param sportId - The sport/eventType ID to filter in-play markets
	 */
	fetchInPlayMarkets(sportId?: string) {
		const params: Record<string, string> = {
			locale: this.helperService.locale,
		}

		if (sportId) {
			params['eventTypeIds'] = sportId
		}

		return this.httpClient
			.get<MarketResponse<Record<string, Market[]>>>(`${MARKET_URL}/bf/in-play-markets`, { params })
			.pipe(map((response) => response.data))
	}

	/**
	 * Get competitions/leagues for a specific sport
	 * @param sportId - The sport/eventType ID
	 */
	getCompetitions(sportId: string) {
		return this.httpClient
			.get<MarketResponse<{ id: string; name: string }[]>>(`${MARKET_URL}/bf/competitions/${sportId}`, {
				params: {
					locale: this.helperService.locale,
				},
			})
			.pipe(map((response) => response.data))
	}

	/**
	 * Get markets for a specific competition/league
	 * @param competitionId - The competition/league ID
	 */
	getCompetitionMarkets(competitionId: string) {
		return this.httpClient
			.get<MarketResponse<Market[]>>(`${MARKET_URL}/bf/competition-markets/${competitionId}`, {
				params: {
					sortBy: 'liquidity',
					locale: this.helperService.locale,
				},
			})
			.pipe(map((response) => response.data))
	}

	/**
	 * Get countries for a specific sport
	 * @param sportId - The sport/eventType ID
	 */
	getCountries(sportId: string) {
		return this.httpClient
			.get<MarketResponse<{ id: string; name: string }[]>>(`${MARKET_URL}/bf/countries/${sportId}`, {
				params: {
					locale: this.helperService.locale,
				},
			})
			.pipe(map((response) => response.data))
	}

	/**
	 * Get leagues for a specific country
	 * @param sportId - The sport/eventType ID
	 * @param countryCode - The country code
	 */
	getLeaguesByCountry(sportId: string, countryCode: string) {
		return this.httpClient
			.get<MarketResponse<{ id: string; name: string }[]>>(`${MARKET_URL}/bf/competitions/${sportId}`, {
				params: {
					countries: countryCode,
					locale: this.helperService.locale,
				},
			})
			.pipe(map((response) => response.data ?? []))
	}

	/**
	 * Get in-play markets from the API endpoint
	 */
	fetchTodayMarkets() {
		return this.httpClient.get<MarketResponse<Record<string, Market[]>>>(`${MARKET_URL}/bf/today-markets`).pipe(map((response) => response.data))
	}

	/**
	 * Get in-play markets from the API endpoint
	 */
	fetchTomorrowMarkets() {
		return this.httpClient.get<MarketResponse<Record<string, Market[]>>>(`${MARKET_URL}/bf/tomorrow-markets`).pipe(map((response) => response.data))
	}

	/**
	 * Check if a market is suspended or closed and return its status
	 * @param marketId
	 * @param marketUpdate
	 */
	isMarketSuspendedOrClosed(marketId: string, marketUpdate: Record<string, MarketUpdate>) {
		const marketStatus = marketUpdate[marketId]?.st
		if (marketStatus && [3, 4].includes(marketStatus)) {
			return marketStatus === 3 ? 'Suspended' : 'Closed'
		}

		return null
	}

	/**
	 * Fetches details for multiple SB markets based on the provided keys.
	 *
	 * @param {string} keys - A string that contains keys or identifiers used to query market details.
	 * @return {Observable<(BookmakerMarket | FancyMarket)[] | undefined>} An observable containing the market response data.
	 */
	getDetailsForMultipleSbMarkets(keys: string): Observable<(BookmakerMarket | FancyMarket)[] | undefined> {
		// getDetailsForMultipleSbMarkets(keys: { providerName: string; eventId: string; providerId: string }[]) {
		return this.httpClient
			.get<MarketResponse<(BookmakerMarket | FancyMarket)[]>>(`${MARKET_URL}/sb/markets`, {
				params: {
					ids: keys,
				},
			})
			.pipe(map((response) => response.data))
	}

	/**
	 * Converts a bookmaker market to a betfair compatible market object
	 * @param bookmakerMarket
	 */
	convertBookmakerMarketToBetfairMarket(bookmakerMarket: BookmakerMarket): Market {
		return {
			marketId: bookmakerMarket.marketId,
			marketName: bookmakerMarket.name,
			marketStartTime: '',
			inPlay: true,
			runners: bookmakerMarket.runners.map((runner) => this.convertBookmakerRunnerToBetfairRunner(runner)),
			eventType: bookmakerMarket.eventType,
			competition: bookmakerMarket.competition,
			event: {
				id: bookmakerMarket.eventId,
				name: bookmakerMarket.eventName,
				countryCode: null,
				openDate: null,
				timezone: null,
				venue: null,
			},
			totalMatched: 0,
			description: {
				bettingType: bookmakerMarket.bettingType,
			},
			navIds: [],
			topic: bookmakerMarket.topic,
			serviceId: bookmakerMarket.serviceId,
			descriptionPrefix: bookmakerMarket.descriptionPrefix,
			minBet: bookmakerMarket.minBet,
			maxBet: bookmakerMarket.maxBet,
		}
	}

	/**
	 * Converts a BookmakerRunner object into a Betfair Runner object.
	 *
	 * @param {BookmakerRunner} bookmakerRunner - The runner object from a bookmaker, containing details such as selectionId, runnerName, handicap, sort priority, and status.
	 * @return {Runner} A Betfair Runner object containing relevant runner details mapped from the bookmakerRunner.
	 */
	convertBookmakerRunnerToBetfairRunner(bookmakerRunner: BookmakerRunner): Runner {
		return {
			selectionId: bookmakerRunner.selectionId,
			runnerName: bookmakerRunner.runnerName,
			handicap: bookmakerRunner.handicap,
			sortPriority: bookmakerRunner.sort,
			metadata: {},
			status: bookmakerRunner.status,
		}
	}

	/**
	 * Converts a fancy market to a betfair compatible market object
	 * @param fancyMarket
	 */
	convertFancyMarketToBetfairMarket(fancyMarket: FancyMarket): Market {
		return {
			marketId: fancyMarket.marketId,
			marketName: fancyMarket.eventName,
			marketStartTime: '',
			inPlay: true,
			runners: fancyMarket.runners.map((runner) => this.convertFancyRunnerToBetfairRunner(runner)),
			eventType: fancyMarket.eventType,
			competition: fancyMarket.competition,
			event: {
				id: fancyMarket.eventId,
				name: fancyMarket.eventName,
				countryCode: null,
				openDate: null,
				timezone: null,
				venue: null,
			},
			totalMatched: 0,
			description: {
				bettingType: fancyMarket.bettingType,
			},
			navIds: [],
			topic: fancyMarket.topic,
			descriptionPrefix: fancyMarket.descriptionPrefix,
		}
	}

	/**
	 * Converts a FancyRunner object to a Betfair Runner object.
	 *
	 * @param {FancyRunner} fancyRunner - The FancyRunner object containing necessary data.
	 * @return {Runner} The transformed Runner object containing Betfair-specific properties.
	 */
	convertFancyRunnerToBetfairRunner(fancyRunner: FancyRunner): Runner {
		return {
			selectionId: Number(fancyRunner.selectionId),
			runnerName: fancyRunner.runnerName,
			handicap: fancyRunner.handicap,
			sortPriority: fancyRunner.priority,
			metadata: {},
			status: fancyRunner.status,
			minBetSize: fancyRunner.minBetSize,
			maxBetSize: fancyRunner.maxBetSize,
		}
	}

	/**
	 * Updates the favorites observable by retrieving the favorite list from the local storage.
	 * Parses the stored JSON string into an array of strings and updates the observable with the result.
	 *
	 * @return {void} This method does not return a value.
	 */
	private updateFavoritesFromLocalStorage(): void {
		this.favorites$.next(JSON.parse(localStorage.getItem('favorites') ?? '[]') as string[])
	}

	/**
	 * Collects and processes bet market details from various sources such as Betfair non-pending bets,
	 * pending bets, and SB bets. Updates the current bet markets are observable with enriched market information.
	 *
	 * The method handles:
	 * - Non-pending Betfair bets: Filters, deduplicates, fetches details, and updates the market information.
	 * - Pending Betfair bets: Filters, deduplicates, fetches details, and updates the market information.
	 * - SB bets: Filters, deduplicates, transforms formats, fetches details, and updates the market information.
	 *
	 * @return {void} This method does not return any value. Updates are applied through subscriptions and state management.
	 */
	private collectBetMarkets(): void {
		// Prepare event details for non-pending betfair bets
		this.betService.nonPendingBets$
			.pipe(
				map((bets) => bets.filter((bet) => bet.provider === MarketProvider.BF)), // Filter only betfair bets
				filter((bets) => bets.length > 0),
				map((bets) => Array.from(new Set(bets.map((bet) => bet.marketId)))), // Make the IDs list be only of unique IDs
				switchMap((marketIds) => this.getDetailsForPendingBfMarkets(marketIds)),
				map((markets) => markets ?? []),
			)
			.subscribe({
				next: (betsMarkets) => {
					const currentBetMarkets = this.betService.betsMarkets$.getValue()

					const tempUnmatchedBetsMarkets: Record<string, Market> = {}
					betsMarkets.forEach((market) => {
						tempUnmatchedBetsMarkets[market.marketId] = market
					})
					this.betService.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets })
				},
			})

		// Prepare event details for pending betfair bets
		this.betService.pendingBets$
			.pipe(
				map((bets) => bets.filter((bet) => bet.provider === MarketProvider.BF)), // Filter only betfair bets
				filter((bets) => bets.length > 0),
				map((bets) => Array.from(new Set(bets.map((bet) => bet.marketId)))), // Make the IDs list be only of unique IDs
				switchMap((marketIds) => this.getDetailsForPendingBfMarkets(marketIds)),
				map((markets) => markets ?? []),
			)
			.subscribe({
				next: (betsMarkets) => {
					const currentBetMarkets = this.betService.betsMarkets$.getValue()

					const tempUnmatchedBetsMarkets: Record<string, Market> = {}
					betsMarkets.forEach((market) => {
						tempUnmatchedBetsMarkets[market.marketId] = market
					})
					this.betService.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets })
				},
			})

		// Prepare event details for SB bets
		this.betService.currentUserOrders$
			.pipe(
				map((bets) => bets.filter((bet) => bet.provider === MarketProvider.SB)), // Filter only SB bets
				filter((bets) => bets.length > 0),
				map((bets) => Array.from(new Set(bets.map((key) => `${key.descriptionPrefix}_${key.eventId}_${key.providerId}`))).join(',')), // Make the IDs list be only of unique IDs
				switchMap((marketIds) => this.getDetailsForMultipleSbMarkets(marketIds)),
				map((markets) => markets ?? []),
			)
			.subscribe({
				next: (markets) => {
					const currentBetMarkets = this.betService.betsMarkets$.getValue()
					const tempUnmatchedBetsMarkets: Record<string, Market> = {}

					markets.forEach((market) => {
						if (market.descriptionPrefix === 'Bookmaker') {
							tempUnmatchedBetsMarkets[market.marketId] = this.convertBookmakerMarketToBetfairMarket(market as BookmakerMarket)
						} else if (market.descriptionPrefix === 'Fancy') {
							tempUnmatchedBetsMarkets[market.marketId] = this.convertFancyMarketToBetfairMarket(market as FancyMarket)
						}
					})

					this.betService.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets })
				},
			})
	}
}
